/**
 * Atouts — arbre d'impact des leviers du jeu (skill tree MMORPG-like).
 *
 * 07/02/2026 — retour user prod : « un arbre d'impact explicatif des
 * atouts représentation des gains par investissements, les combinés
 * supports etc... comme un arbre de compétences dans les MMORPG ».
 *
 * Représentation :
 *   - Graphe React Flow horizontal, 3 colonnes :
 *       (Support)  →  (Investissement / Atout dépôt)  →  (Effet métier)
 *   - Chaque nœud porte son bonus brut en % et son état (débloqué / actif).
 *   - Edges = prérequis (graphes stricts d'INVESTMENTS) et combos métier
 *     connus par le moteur (paires support+investissement qui se renforcent).
 *
 * Pas d'interaction d'achat depuis cet écran : c'est un outil
 * d'exploration / planification. Pour recruter ou investir, cliquer un
 * nœud ouvre la page dédiée via le bouton « Voir détail ».
 */
import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MarkerType, Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Sparkles, Briefcase, Building2, Wrench, Lock, CheckCircle2, Circle, ExternalLink, Table2, Network } from 'lucide-react';
import useGameStore from '../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../store/stableEmpties';
import { SUPPORT_ROLES, evaluatePrerequisite } from '../data/supportRoles';
import { INVESTMENTS } from '../data/investments';
import PhasesPanel from '../components/PhasesPanel';
import { useTranslation } from '../i18n';

const KIND_STYLES = {
  support: {
    label: 'Support',
    border: 'border-purple-400',
    bg: 'bg-purple-500/10',
    Icon: Briefcase,
  },
  investment: {
    label: 'Investissement',
    border: 'border-sky-400',
    bg: 'bg-sky-500/10',
    Icon: Building2,
  },
  combo: {
    label: 'Combo',
    border: 'border-amber-400',
    bg: 'bg-amber-500/10',
    Icon: Sparkles,
  },
};

const STATE_BADGE = {
  active:    { bg: 'bg-emerald-500/20', text: 'text-emerald-200', border: 'border-emerald-400', Icon: CheckCircle2, label: 'Actif' },
  available: { bg: 'bg-sky-500/20',     text: 'text-sky-200',     border: 'border-sky-400',     Icon: Circle,       label: 'À débloquer' },
  locked:    { bg: 'bg-slate-700',      text: 'text-slate-400',   border: 'border-slate-600',   Icon: Lock,         label: 'Verrouillé' },
};

/**
 * Formats a support's bonus list into a compact string for display.
 */
function formatSupportBonus(role) {
  if (!Array.isArray(role.bonuses) || role.bonuses.length === 0) return '—';
  return role.bonuses.map(b => {
    if (b.type === 'revenue') return `+${Math.round((b.value || 0) * 100)} % revenus`;
    if (b.type === 'expense_reduction') return `−${Math.round((b.value || 0) * 100)} % charges`;
    if (b.type === 'reputation_passive') return `+${b.value} rép. / mois`;
    if (b.type === 'satisfaction_passive') return `+${b.value} satisfaction / mois`;
    if (b.type === 'driver_xp') return `+${Math.round((b.value || 0) * 100)} % XP équipe`;
    return b.label || b.type;
  }).join(' · ');
}

function formatInvestmentBonus(inv) {
  if (Number.isFinite(inv.impactMultiplier) && inv.impactMultiplier !== 0) {
    const pct = Math.round(inv.impactMultiplier * 100);
    return `${pct > 0 ? '+' : ''}${pct} %`;
  }
  return inv.impactRevenue ? '🔓 déblocage' : '—';
}

// Combo paires support × investissement connus du moteur (lecture : si X est
// recruté ET Y est actif, on multiplie le bonus de Y par 1.10 — bonus
// pédagogique pour visualiser la synergie).
const COMBO_DEFS = [
  // Marketing : Léa + Site web → +10 % de conversion
  { id: 'combo-lea-site', supportId: 'role-lea-bertrand', invId: 'inv-tech-02', label: 'Léa + Site web', boost: '+10 % conversion' },
  // Marc + Campagne marketing → +15 % contrats
  { id: 'combo-marc-campagne', supportId: 'role-marc-dupuis', invId: 'inv-tech-03', label: 'Marc + Campagne marketing', boost: '+15 % contrats' },
  // Achats : Karim + Contrat fournisseur fuel → −5 % fuel cumulé
  { id: 'combo-karim-fuel', supportId: 'role-karim-benali', invId: 'inv-energy-03', label: 'Karim + Contrat fuel', boost: '−5 % carburant cumulé' },
  // Admin : Nathalie + Logiciel TMS → +12 % cumul facturation/planif
  { id: 'combo-nathalie-tms', supportId: 'role-nathalie-robert', invId: 'inv-tech-01', label: 'Nathalie + TMS', boost: '+12 % productivité admin' },
  // Exploitation : Rémi + Quai dédié → +15 % efficacité
  { id: 'combo-remi-quai', supportId: 'role-christophe-faure', invId: 'inv-depot-04', label: 'Rémi + Quai dédié', boost: '+15 % efficacité' },
];

function NodeCard({ data }) {
  const KIND = KIND_STYLES[data.kind] || KIND_STYLES.investment;
  const STATE = STATE_BADGE[data.state] || STATE_BADGE.locked;
  const Icon = KIND.Icon;
  const StateIcon = STATE.Icon;
  return (
    <div
      className={`w-56 rounded-xl border-2 ${KIND.border} ${KIND.bg} p-3 shadow-lg backdrop-blur-sm relative`}
      data-testid={`atout-node-${data.id}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-slate-400" />
      <Handle type="source" position={Position.Right} className="!bg-slate-400" />
      <div className="flex items-start gap-2 mb-1.5">
        <div className={`p-1.5 rounded-md ${KIND.bg} ${KIND.border} border flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5 text-slate-100" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-mono text-slate-300">{KIND.label}</p>
          <p className="text-sm font-bold text-white leading-tight truncate" title={data.title}>{data.title}</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-200 mb-1.5 line-clamp-2" title={data.subtitle}>
        {data.subtitle}
      </p>
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${STATE.bg} ${STATE.text} border ${STATE.border} uppercase tracking-wider font-bold`}>
          <StateIcon className="w-2.5 h-2.5" />
          {STATE.label}
        </span>
        <span className="text-[11px] font-mono font-bold text-amber-200">
          {data.bonusStr || '—'}
        </span>
      </div>
      {data.cta && (
        <button
          type="button"
          onClick={data.cta.onClick}
          className="mt-2 w-full text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-slate-800/60 hover:bg-slate-700 text-slate-200 border border-slate-600 inline-flex items-center justify-center gap-1 transition-colors"
          data-testid={`atout-cta-${data.id}`}
        >
          {data.cta.label} <ExternalLink className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

const nodeTypes = { atoutCard: NodeCard };

export default function Atouts() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  const state = useGameStore();

  // Précalcul du statut de chaque nœud (active / available / locked).
  const supportNodes = useMemo(() => {
    return SUPPORT_ROLES.map((role, idx) => {
      const isActive = !!hiredSupport[role.id];
      const prereqOk = evaluatePrerequisite(role, state).ok;
      const stateLabel = isActive ? 'active' : (prereqOk ? 'available' : 'locked');
      return {
        id: `s-${role.id}`,
        type: 'atoutCard',
        position: { x: 0, y: idx * 200 },
        data: {
          id: role.id,
          kind: 'support',
          title: role.name,
          subtitle: `${role.role} · ${role.familyId}`,
          bonusStr: formatSupportBonus(role),
          state: stateLabel,
          cta: { label: 'Voir Fonctions Support', onClick: () => navigate('/fonctions-support') },
        },
      };
    });
  }, [hiredSupport, state, navigate]);

  const investmentNodes = useMemo(() => {
    return INVESTMENTS.map((inv, idx) => {
      const isActive = !!activeInvestments[inv.id];
      const condOk = !inv.conditionCheck || inv.conditionCheck(state);
      const stateLabel = isActive ? 'active' : (condOk ? 'available' : 'locked');
      return {
        id: `i-${inv.id}`,
        type: 'atoutCard',
        position: { x: 380, y: idx * 200 },
        data: {
          id: inv.id,
          kind: 'investment',
          title: inv.name,
          subtitle: `${inv.category}${inv.condition ? ` · ${inv.condition}` : ''}`,
          bonusStr: formatInvestmentBonus(inv),
          state: stateLabel,
          cta: { label: 'Voir investissements', onClick: () => navigate('/fonctions-support') },
        },
      };
    });
  }, [activeInvestments, state, navigate]);

  const comboNodes = useMemo(() => {
    return COMBO_DEFS.map((combo, idx) => {
      const supportActive = !!hiredSupport[combo.supportId];
      const invActive = !!activeInvestments[combo.invId];
      const stateLabel = supportActive && invActive ? 'active' : (supportActive || invActive ? 'available' : 'locked');
      return {
        id: `c-${combo.id}`,
        type: 'atoutCard',
        position: { x: 760, y: idx * 200 },
        data: {
          id: combo.id,
          kind: 'combo',
          title: combo.label,
          subtitle: 'Synergie support × investissement',
          bonusStr: combo.boost,
          state: stateLabel,
        },
      };
    });
  }, [hiredSupport, activeInvestments]);

  const nodes = useMemo(() => [...supportNodes, ...investmentNodes, ...comboNodes], [supportNodes, investmentNodes, comboNodes]);

  // Edges : prérequis stricts entre investissements + combos support→investissement.
  const edges = useMemo(() => {
    const out = [];
    // Investissement → investissement (prérequis hardcodés via conditionCheck) :
    // pour rester maintenable, on extrait les paires connues des string `condition`.
    // Heuristique : si la condition mentionne un autre nom d'investissement, on
    // trace l'arête.
    INVESTMENTS.forEach(inv => {
      INVESTMENTS.forEach(other => {
        if (inv.id === other.id) return;
        if ((inv.condition || '').toLowerCase().includes(other.name.toLowerCase().split('(')[0].trim().toLowerCase())) {
          out.push({
            id: `e-inv-${other.id}-${inv.id}`,
            source: `i-${other.id}`,
            target: `i-${inv.id}`,
            type: 'smoothstep',
            animated: !!activeInvestments[other.id] && !activeInvestments[inv.id],
            style: { stroke: 'rgb(125 211 252 / 0.7)', strokeWidth: 1.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: 'rgb(125 211 252)' },
          });
        }
      });
    });
    // Combo : support → combo et investissement → combo.
    COMBO_DEFS.forEach(combo => {
      out.push({
        id: `e-combo-s-${combo.id}`,
        source: `s-${combo.supportId}`,
        target: `c-${combo.id}`,
        type: 'smoothstep',
        animated: !!hiredSupport[combo.supportId],
        style: { stroke: 'rgb(168 85 247 / 0.7)', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: 'rgb(168 85 247)' },
      });
      out.push({
        id: `e-combo-i-${combo.id}`,
        source: `i-${combo.invId}`,
        target: `c-${combo.id}`,
        type: 'smoothstep',
        animated: !!activeInvestments[combo.invId],
        style: { stroke: 'rgb(125 211 252 / 0.7)', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: 'rgb(125 211 252)' },
      });
    });
    return out;
  }, [activeInvestments, hiredSupport]);

  // Stats globales en haut.
  const stats = useMemo(() => {
    const supportActive = SUPPORT_ROLES.filter(r => hiredSupport[r.id]).length;
    const supportAvailable = SUPPORT_ROLES.filter(r => !hiredSupport[r.id] && evaluatePrerequisite(r, state).ok).length;
    const invActive = INVESTMENTS.filter(i => activeInvestments[i.id]).length;
    const invAvailable = INVESTMENTS.filter(i => !activeInvestments[i.id] && (!i.conditionCheck || i.conditionCheck(state))).length;
    const comboActive = COMBO_DEFS.filter(c => hiredSupport[c.supportId] && activeInvestments[c.invId]).length;
    return { supportActive, supportAvailable, invActive, invAvailable, comboActive, comboTotal: COMBO_DEFS.length };
  }, [hiredSupport, activeInvestments, state]);

  const handleNodeClick = useCallback((_e, node) => {
    // Naviguer vers la page dédiée pour les supports et investissements.
    if (node.data?.cta?.onClick) {
      node.data.cta.onClick();
    }
  }, []);

  // 08/02/2026 — Q2 user prod : toggle Arbre / Tableau. Le tableau est
  // un rendu condensé sans ReactFlow (utile sur mobile + lecture rapide).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('hst-atouts-view') || 'tree'; } catch { return 'tree'; }
  });
  const handleViewChange = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('hst-atouts-view', mode); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4" data-testid="atouts-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-black tracking-tight text-foreground inline-flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-amber-300" /> {t('phases_atouts_title')}
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            {t('phases_atouts_intro')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <StatPill label="Support actif" value={`${stats.supportActive} / ${SUPPORT_ROLES.length}`} color="emerald" testId="atouts-stat-support-active" />
          <StatPill label="Support dispo" value={stats.supportAvailable} color="sky" testId="atouts-stat-support-avail" />
          <StatPill label="Investissements actifs" value={`${stats.invActive} / ${INVESTMENTS.length}`} color="emerald" testId="atouts-stat-inv-active" />
          <StatPill label="Investissements dispo" value={stats.invAvailable} color="sky" testId="atouts-stat-inv-avail" />
          <StatPill label="Combos actifs" value={`${stats.comboActive} / ${stats.comboTotal}`} color="amber" testId="atouts-stat-combo-active" />
        </div>
      </header>

      <PhasesPanel currentPhase={state.phase || 1} hiredSupport={hiredSupport} />

      {/* 08/02/2026 — Toggle Arbre / Tableau (persisté en localStorage). */}
      <div className="flex items-center gap-1 p-1 rounded-lg border border-ink-600 bg-ink-800 w-fit" data-testid="atouts-view-toggle">
        <button
          type="button"
          onClick={() => handleViewChange('tree')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] uppercase tracking-wider font-bold transition-all ${
            viewMode === 'tree' ? 'bg-amber-500/30 text-amber-100 border border-amber-400/60' : 'text-slate-400 hover:text-slate-200'
          }`}
          data-testid="atouts-view-tree"
          aria-pressed={viewMode === 'tree'}
        >
          <Network className="w-3.5 h-3.5" /> Arbre
        </button>
        <button
          type="button"
          onClick={() => handleViewChange('table')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] uppercase tracking-wider font-bold transition-all ${
            viewMode === 'table' ? 'bg-amber-500/30 text-amber-100 border border-amber-400/60' : 'text-slate-400 hover:text-slate-200'
          }`}
          data-testid="atouts-view-table"
          aria-pressed={viewMode === 'table'}
        >
          <Table2 className="w-3.5 h-3.5" /> Tableau
        </button>
      </div>

      {viewMode === 'tree' ? (
        <div className="rounded-xl border border-ink-600 bg-ink-900 overflow-hidden" style={{ height: '70vh' }} data-testid="atouts-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{ padding: 0.1 }}
            minZoom={0.2}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} color="rgb(71 85 105 / 0.3)" />
            <Controls position="bottom-right" />
          </ReactFlow>
        </div>
      ) : (
        <AtoutsTable
          supportNodes={supportNodes}
          investmentNodes={investmentNodes}
          comboNodes={comboNodes}
          navigate={navigate}
        />
      )}

      <section className="rounded-lg border border-ink-600 bg-ink-800 p-4" data-testid="atouts-legend">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-200 mb-2">Comment lire l&apos;arbre ?</h2>
        <ul className="text-[11px] text-slate-300 space-y-1 list-disc list-inside">
          <li><span className="text-purple-200 font-bold">Colonne gauche</span> — fonctions support (RH externes recrutables).</li>
          <li><span className="text-sky-200 font-bold">Colonne milieu</span> — investissements infrastructure (dépôt, énergie, bureau, tech).</li>
          <li><span className="text-amber-200 font-bold">Colonne droite</span> — combos : un support + un investissement spécifique qui se renforcent mutuellement.</li>
          <li>Flèches <span className="text-sky-300">bleues animées</span> = prérequis déjà rempli ; flèches statiques = lien théorique non encore débloqué.</li>
          <li>Cliquer un nœud support / investissement t&apos;emmène vers la page de recrutement / d&apos;achat.</li>
        </ul>
      </section>
    </div>
  );
}

function StatPill({ label, value, color, testId }) {
  const colors = {
    emerald: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40',
    sky:     'bg-sky-500/10 text-sky-200 border-sky-500/40',
    amber:   'bg-amber-500/10 text-amber-200 border-amber-500/40',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border font-mono ${colors[color] || colors.sky}`}
      data-testid={testId}
    >
      <span className="opacity-70 uppercase tracking-wider text-[9px]">{label}</span>
      <span className="font-bold">{value}</span>
    </span>
  );
}

/**
 * 08/02/2026 — Vue tableau (alternative au ReactFlow). 3 tableaux empilés
 * (Supports / Investissements / Combos). Chaque ligne montre le nom, le
 * bonus, l'état et un bouton vers la page dédiée. Bien plus lisible que
 * l'arbre sur mobile et pour le scan rapide « qu'est-ce que je peux
 * débloquer ? ».
 */
function AtoutsTable({ supportNodes, investmentNodes, comboNodes, navigate }) {
  return (
    <div className="space-y-4" data-testid="atouts-table">
      <AtoutTableSection
        title="Fonctions Support"
        accent="purple"
        rows={supportNodes}
        ctaLabel="Recruter"
        onCta={() => navigate('/fonctions-support')}
        testId="atouts-table-support"
      />
      <AtoutTableSection
        title="Investissements"
        accent="sky"
        rows={investmentNodes}
        ctaLabel="Investir"
        onCta={() => navigate('/fonctions-support')}
        testId="atouts-table-investments"
      />
      <AtoutTableSection
        title="Combos (Synergies)"
        accent="amber"
        rows={comboNodes}
        ctaLabel={null}
        testId="atouts-table-combos"
      />
    </div>
  );
}

function AtoutTableSection({ title, accent, rows, ctaLabel, onCta, testId }) {
  const accentMap = {
    purple: 'border-purple-500/40 bg-purple-500/5',
    sky:    'border-sky-500/40 bg-sky-500/5',
    amber:  'border-amber-500/40 bg-amber-500/5',
  };
  const accentText = {
    purple: 'text-purple-200',
    sky:    'text-sky-200',
    amber:  'text-amber-200',
  };
  return (
    <section
      className={`rounded-xl border ${accentMap[accent]} overflow-hidden`}
      data-testid={testId}
    >
      <header className={`px-4 py-2 border-b border-ink-600 ${accentMap[accent]} flex items-center justify-between`}>
        <h2 className={`text-sm font-bold uppercase tracking-wider ${accentText[accent]}`}>{title}</h2>
        <span className="text-[10px] font-mono text-slate-400">
          {rows.filter(r => r.data.state === 'active').length} actif{rows.filter(r => r.data.state === 'active').length > 1 ? 's' : ''} / {rows.length} total
        </span>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid={`${testId}-table`}>
          <thead className="bg-ink-700/40 text-slate-400 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Nom</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Bonus</th>
              <th className="px-3 py-2 text-center">État</th>
              {ctaLabel && <th className="px-3 py-2 text-right">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/60">
            {rows.length === 0 && (
              <tr><td colSpan={ctaLabel ? 5 : 4} className="px-3 py-4 text-center text-slate-500 italic">
                Aucune entrée.
              </td></tr>
            )}
            {rows.map(node => {
              const STATE = STATE_BADGE[node.data.state] || STATE_BADGE.locked;
              const StateIcon = STATE.Icon;
              return (
                <tr
                  key={node.id}
                  className="hover:bg-ink-700/30 transition-colors"
                  data-testid={`atouts-table-row-${node.data.id}`}
                >
                  <td className="px-3 py-2 font-bold text-slate-100">{node.data.title}</td>
                  <td className="px-3 py-2 text-slate-400">{node.data.subtitle}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-amber-200">{node.data.bonusStr || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${STATE.bg} ${STATE.text} border ${STATE.border} uppercase tracking-wider font-bold`}>
                      <StateIcon className="w-2.5 h-2.5" />
                      {STATE.label}
                    </span>
                  </td>
                  {ctaLabel && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={onCta}
                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-slate-800/60 hover:bg-slate-700 text-slate-200 border border-slate-600 inline-flex items-center gap-1 transition-colors"
                        data-testid={`atouts-table-cta-${node.data.id}`}
                      >
                        {ctaLabel} <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
